import React, { Fragment } from "react";
import {
  CircularProgress,
  Paper,
  Snackbar,
  SnackbarContent,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
} from "@mui/material";
import { useResavenuePricing } from "../hooks/useResavenuePricing";
import { styled } from "@mui/system";
import { Tabhead, TCell, TextFieldMedium, TRow } from "./Styles";

export default function ResavenuePricingSetup({ isResavenue }) {
  const {
    ratePlans,
    changedCells,
    changeHandler,
    handleSaveRatePlans,
    isFetching,
    isLoading,
    snackbarMsg,
    setSnackBarMsg,
  } = useResavenuePricing();

  const columns = [
    { key: "minThresholdWeekday", label: "Min Threshold Weekday" },
    { key: "maxThresholdWeekday", label: "Max Threshold Weekday" },
    { key: "minThresholdWeekend", label: "Min Threshold Weekend" },
    { key: "maxThresholdWeekend", label: "Max Threshold Weekend" },
    { key: "minThresholdWeekdayLow", label: "Min Threshold Weekday" },
    { key: "maxThresholdWeekdayLow", label: "Max Threshold Weekday" },
    { key: "minThresholdWeekendLow", label: "Min Threshold Weekend" },
    { key: "maxThresholdWeekendLow", label: "Max Threshold Weekend" },
  ];

  const tableBodyColumns = [
    "addToBasePriceLow",
    "addToBasePriceHigh",
    "minThresholdWeekday",
    "maxThresholdWeekday",
    "minThresholdWeekend",
    "maxThresholdWeekend",
    "minThresholdWeekdayLow",
    "maxThresholdWeekdayLow",
    "minThresholdWeekendLow",
    "maxThresholdWeekendLow",
  ];
  if (isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center" p={4} height={"50vh"}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack>
      <TableContainer style={{ maxHeight: "70vh" }} component={Paper}>
        <Table
          stickyHeader
          aria-label="rate plan table"
          style={{
            width: "100%",
            border: "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <THead>
            <TRow > 
              <CustomTabHead rowSpan={2}>
                {isResavenue ? "Rate Plan Name" : "Room Name"}
              </CustomTabHead>
              <CustomTabHead rowSpan={2}>Add to Base Price (Low)</CustomTabHead>
              <CustomTabHead rowSpan={2}>
                Add to Base Price (High)
              </CustomTabHead>
              <CustomTabHead colSpan={4}>High Season</CustomTabHead>
              <CustomTabHead colSpan={4}>Low Season</CustomTabHead>
            </TRow>
            <TRow>
              {columns.map((col) => (
                <SubHeaderCell
                  key={col.key}
                  rowSpan={col.rowSpan}
                  sx={{ borderRadius: "0px !important", height: "35px" }}
                >
                  {col.label}
                </SubHeaderCell>
              ))}
            </TRow>
          </THead>

          <TableBody>
            {ratePlans.map((plan, rowIndex) => (
              <Fragment key={plan.RoomRatePlanId}>
                <TRow>
                  <CustomTCell>
                    {isResavenue
                      ? `${plan.roomRatePlanName} ${plan.occupancyType}`
                      : `${plan.roomName}`}
                  </CustomTCell>

                  {tableBodyColumns.map((col, index) => {
                    const isBaseRoom =
                      plan.isBaseRoom &&
                      (col === "addToBasePriceLow" ||
                        col === "addToBasePriceHigh");

                    const isChanged = changedCells.has(`${rowIndex}-${col}`);

                    return (
                      <CustomTCell
                        key={`${col} - ${index}`}
                        $ischanged={isChanged}
                        sx={{ backgroundColor: isBaseRoom ? "#8282824D" : "" }}
                      >
                        {isBaseRoom ? (
                          <Typography>MASTER</Typography>
                        ) : (
                          <TextFieldMedium
                            type="number"
                            value={plan[col] ?? ""}
                            onChange={(e) =>
                              changeHandler(rowIndex, col, e.target.value)
                            }
                          />
                        )}
                      </CustomTCell>
                    );
                  })}
                </TRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ButtonWrapper>
        <SaveButton onClick={handleSaveRatePlans} disabled={isLoading}>
          {isLoading ? "Saving..." : "Save"}
        </SaveButton>
      </ButtonWrapper>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={snackbarMsg.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackBarMsg({
            ...snackbarMsg,
            open: false,
          });
        }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: snackbarMsg.color,
            textTransform: "capitalize",
          }}
          message={snackbarMsg.msg}
        />
      </Snackbar>
    </Stack>
  );
}

/* ---------------- Styled Components ---------------- */

const CustomTCell = styled(TCell)`
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: ${(props) =>
    props.$ischanged ? "rgba(48, 111, 188, 0.15)" : "transparent"};
  transition: background-color 0.3s ease;
`;

const THead = styled(TableHead)`
  background: #f9f9f9;
`;

const ButtonWrapper = styled("div")`
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
`;

const SaveButton = styled("button")`
  padding: 10px 20px;
  background: rgb(48, 111, 188);
  color: #fff;
  font: normal normal bold 14px/18px Roboto;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: rgb(38, 90, 152);
  }

  &:disabled {
    background: #90caf9;
    cursor: not-allowed;
  }
`;

const CustomTabHead = styled(Tabhead)`
  &.MuiTableCell-root {
    position: sticky;
    top: 0;
    z-index: 3; 
    height: 35px !important;
    border: 0.5px solid #ffffff;
    font-weight: 600;
  }
`;

const SubHeaderCell = styled(Tabhead)`
  &.MuiTableCell-root {
    position: sticky;
    top: 35px;/* height of first header row */
    z-index: 3;
    height: 35px !important;
    border: 0.5px solid #ffffff;
  }
`;

